import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import OrderSearchSection from "../../../../../../components/search-sections/order";
import {routeFunctions, routes} from "../../../../../../../core/models/static/routes/app-routes";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import Api from "../../../../../../../core/services/api/api";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import {InvoicesListsItemsDataGridCellKeys, InvoicesListsOrdersDataGridCellKeys, InvoicesListsReportDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import InvoicesListViewOrdersTable from "./sections/orders";
import InvoicesListViewItemsTable from "./sections/items";
import InvoicesListViewReportTable from "./sections/report";
import {useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {Link, useHistory} from "react-router-dom";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import Button from "../../../../../../components/base/button/button";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import useQuery from "../../../../../../hooks/use-query";
import {useAppSelector} from "../../../../../../redux";
import CreateUserOnlineProfileDialog from "../../../../../../components/dialogs/create-user-online-profile";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import PrintOrderConfigurationsDialog from "../../../../../../components/dialogs/print-order-configurations";
import {stringifyUrl} from "query-string";

const tabs = {
    orders: 'orders',
    items: 'items',
    report: 'report',
};

const tabsList = [
    {
        title: "Orders",
        value: tabs.orders,
    },
    {
        title: "Items",
        value: tabs.items,
    },
    {
        title: "Report",
        value: tabs.report,
    }
]
const defaultTab = tabs.orders;

const ordersOrderByPropNames = Object.values(InvoicesListsOrdersDataGridCellKeys).filter(e => e !== InvoicesListsOrdersDataGridCellKeys.navigate && e !== InvoicesListsOrdersDataGridCellKeys.print)
const itemsOrderByPropNames = Object.values(InvoicesListsItemsDataGridCellKeys).filter(e => e !== InvoicesListsItemsDataGridCellKeys.navigate && e !== InvoicesListsOrdersDataGridCellKeys.print);
const reportOrderByPropNames = Object.values(InvoicesListsReportDataGridCellKeys).filter(e => [
    InvoicesListsReportDataGridCellKeys.avgCost,
    InvoicesListsReportDataGridCellKeys.discountAmount,
    InvoicesListsReportDataGridCellKeys.appliedGiftCardAmount,
    InvoicesListsReportDataGridCellKeys.subtotal,
].includes(e));

const exportingChunkSize = 100;

const initNewOnlineProfileDialogState = {open: false, initialValues: null, onSuccess: () => void 0, onClose: () => void 0};
const initPrintOrderConfigurationsDialogState = {open: false, printUrl: '', onClose: () => void 0};

const InvoicesListView = ({allowPayments = false}) => {
    const history = useHistory();
    const [query] = useQuery()
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const [updatedTab, setUpdatedTab] = useState(query.tab ?? defaultTab);
    const [fetchingOrdersCustomer, setFetchingOrdersCustomer] = useState([]);
    const [onlineUserProfileDialog, setOnlineUserProfileDialog] = useState(initNewOnlineProfileDialogState);
    const [printOrderConfigurationsDialog, setPrintOrderConfigurationsDialog] = useState(initPrintOrderConfigurationsDialogState);

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('invoices-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        if (defaultCustomer?.code) {
            configureNotebookTags((tags) => tags.customerSalesInvoices(defaultCustomer?.code));
            configureBehavioralTool((tags) => tags.customerSalesInvoices(defaultCustomer?.code));
        } else {
            configureNotebookTags((tags) => tags.salesInvoices);
            configureBehavioralTool((tags) => tags.salesInvoices);
        }
    }, [configureNotebookTags, configureBehavioralTool, defaultCustomer]);

    /**
     * Prepares the search request for search api with the given parameters.
     *
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareSearchData = useCallback((args) => {
        const res = {
            filters: {
                customerId: defaultCustomer?.id ? [defaultCustomer?.id] : args.customers?.map(e => e.id),
                fromDate: args.filters?.startDate,
                ToDate: args.filters?.endDate ?? DateUtils.serverNow().add('1', 'day').endOf('day').format(DateUtils.withoutOffsetFormat),
                Keyword: args.filters?.keyword?.toString(),
            },
            paginationInfo: args.paginationInfo,
        };
        // only when the items tab is visible, we use partNumber field
        if (args.tab === tabs.items) {
            res.filters['partNo'] = args.filters?.partNo;
        }
        if (args.orderBy) {
            switch (args.tab) {
                case tabs.report:
                    if (reportOrderByPropNames.includes(args.orderBy.field)) {
                        res['orderByProperties'] = [{
                            propertyName: args.orderBy.field,
                            isDescending: args.orderBy.descending
                        }]
                    }
                    break;
                case tabs.items:
                    if (itemsOrderByPropNames.includes(args.orderBy.field)) {
                        res['orderByProperties'] = [{
                            propertyName: args.orderBy.field,
                            isDescending: args.orderBy.descending
                        }]
                    }
                    break;
                case tabs.orders:
                default:
                    if (ordersOrderByPropNames.includes(args.orderBy.field)) {
                        res['orderByProperties'] = [{
                            propertyName: args.orderBy.field,
                            isDescending: args.orderBy.descending
                        }]
                    }
                    break;
            }
        }
        return res;
    }, [defaultCustomer?.id])

    /**
     * Fetches the orders based on the given parameters.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchOrders = useCallback(async (args, forExport = false) => {
        const forApi = prepareSearchData(args);
        let response;
        switch (args.tab) {
            case tabs.items:
                response = await Api.searchInvoiceItems(forApi, args.cancelTokenSource?.token);
                break;
            case tabs.orders:
            case tabs.report:
            default:
                response = await Api.searchInvoices(forApi, args.cancelTokenSource?.token);
                break;
        }
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.resultList ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.paginationInfo?.length ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareSearchData])

    /**
     * Searches among the orders based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const searchOrders = useCallback(async (result) => {
        await fetchOrders(result, false);
    }, [fetchOrders]);


    const [ordersDataGridSaveConfig, itemsDataGridSaveConfig, reportDataGridSaveConfig] = useMemo(() => [
        ({
            name: (allowPayments ? 'Accounting' : 'Sales').concat('InvoicesOrdersListViewOrdersDataGrid'),
            version: 1,
        }),
        ({
            name: (allowPayments ? 'Accounting' : 'Sales').concat('InvoicesOrdersListViewItemsDataGrid'),
            version: 1,
        }),
        ({
            name: (allowPayments ? 'Accounting' : 'Sales').concat('InvoicesOrdersListViewReportDataGrid'),
            version: 1,
        })
    ], [allowPayments])

    const [, ordersDataGridSortBy, ordersDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: ordersDataGridSaveConfig.name,
        version: ordersDataGridSaveConfig.version,
    });
    const [, itemsDataGridSortBy, itemsDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: itemsDataGridSaveConfig.name,
        version: itemsDataGridSaveConfig.version,
    });
    const [, reportDataGridSortBy, reportDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: reportDataGridSaveConfig.name,
        version: reportDataGridSaveConfig.version,
    });
    const [dataGridSortBy, dataGridPageSize] = useMemo(() => {
        switch (updatedTab) {
            case tabs.orders:
                return [ordersDataGridSortBy, ordersDataGridPageSize]
            case tabs.items:
                return [itemsDataGridSortBy, itemsDataGridPageSize]
            case tabs.report:
                return [reportDataGridSortBy, reportDataGridPageSize]
            default:
                return [];
        }
    }, [itemsDataGridPageSize, itemsDataGridSortBy, ordersDataGridPageSize, ordersDataGridSortBy, updatedTab, reportDataGridSortBy, reportDataGridPageSize])

    const initialPaginationInfo = useMemo(() => ({
        ...(dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize,} : DefaultPaginationInfo),
        sizes: [10, 20, 30],
    }), [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        customers: [],
    }), [updatedTab, initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(searchOrders, initialSearchDataSettings)

    /**
     * Formats the exported data's entries and returns the info to be exported.
     * @param {any[]} list
     * @return {DataGridExportInfo}
     */
    const formatExportingData = useCallback((list) => {
        switch (searchData.tab) {
            case tabs.report:
                return {
                    title: `Invoices Report`,
                    body: list?.map(e => ({
                        'Invoice No': e.invoiceNo ?? '',
                        'Customer': e.customer?.name ?? '',
                        'Invoice Date': e.invoiceDate ? DateUtils.serverToLocal(e.invoiceDate).format(DateUtils.dateFormat) : '',
                        'PO-Number': e.customerPO ?? '',
                        'Sales Person': e.salespersonNo ?? '',
                        'Avg Cost': formatMoney(e.avgCost ?? ''),
                        'Discount': formatMoney(e.discountAmount ?? ''),
                        'Applied Gift Card': formatMoney(e.appliedGiftCardAmount ?? ''),
                        'Sub Total': formatMoney(e.subTotal ?? ''),
                        'Total': formatMoney(e.total ?? ''),
                        'Balance': formatMoney(e.payInfo?.minimumRequiredPayment ?? ''),
                        'Status': e.status?.name ?? '',
                    }))
                }
            case tabs.items:
                return {
                    title: `Invoice Items`,
                    body: list?.map(e => ({
                        'Invoice No': e.orderNo ?? '',
                        'Customer': e.customer?.name ?? '',
                        'Part Number': e.partNo ?? '',
                        'Invoice Date': e.createdDate ? DateUtils.serverToLocal(e.createdDate).format(DateUtils.dateFormat) : '',
                        'Unit Price': formatMoney(e.unitPrice ?? ''),
                        'Order Quantity': e.orderQty ?? '',
                    })) ?? [],
                }
            case tabs.orders:
            default:
                return {
                    title: `Invoices`,
                    body: list?.map(e => ({
                        'Invoice No': e.invoiceNo ?? '',
                        'Customer': e.customer?.name ?? '',
                        'Invoice Date': e.invoiceDate ? DateUtils.serverToLocal(e.invoiceDate).format(DateUtils.dateFormat) : '',
                        'PO-Number': e.customerPO ?? '',
                        'Sales Person No': e.salespersonNo ?? '',
                        'Total Ordered': formatMoney(e.total ?? ''),
                        'Status': e.status?.name ?? '',
                    })) ?? [],
                }
        }
    }, [searchData.tab])

    /**
     * Exports the entries of this view by searching over all the entries in the system.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const canExportInvoicesList = operatorAccessLevels.packages.sales.exportInvoicesList;
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchOrders({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [formatExportingData, isMounted, searchData, fetchOrders])

    /**
     * With each change in the [tab], updates the value of the [updatedTab] state.
     */
    useLayoutEffect(() => setUpdatedTab(searchData.tab), [searchData.tab])

    /**
     * Handles changes in the tabs of the orders view.
     */
    const onTabChanged = useCallback((e, newTab) => {
        setData([]);
        searchData.setTab(newTab);
        switch (newTab) {
            case tabs.orders:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: ordersDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(ordersDataGridSortBy);
                break;
            case tabs.items:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: itemsDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(itemsDataGridSortBy);
                break;
            case tabs.report:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: reportDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(reportDataGridSortBy);
                break;
            default:
                return;
        }
    }, [initialPaginationInfo.pageSize, itemsDataGridPageSize, itemsDataGridSortBy, ordersDataGridPageSize, ordersDataGridSortBy, reportDataGridPageSize, reportDataGridSortBy, searchData])

    /**
     * Fetches the customer id from the given code.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchCustomerIdFromCode = useCallback(async (customer, orderId) => {
        setFetchingOrdersCustomer(prevState => [...prevState, orderId]);
        const response = await Api.getCustomerSimpleInfoByCode(customer.customerNo);
        if (!isMounted())
            return;
        setFetchingOrdersCustomer(prevState => prevState.filter(e => e !== orderId));
        if (response.resultFlag) {
            return response.data?.id;
        } else {
            let succeeded = false;
            return await new Promise(resolve => {
                setOnlineUserProfileDialog({
                    open: true,
                    initialValues: {company: {id: customer.id, code: customer.customerNo, name: customer.name}},
                    onClose: () => {
                        if (!succeeded) {
                            resolve();
                        }
                        setOnlineUserProfileDialog(initNewOnlineProfileDialogState);
                    },
                    onSuccess: async () => {
                        succeeded = true;
                        const response = await Api.getCustomerSimpleInfoByCode(customer.customerNo);
                        resolve(response.data?.id);
                    }
                });
            })
        }
    }, [isMounted])

    /**
     * Navigates to the information view of the given customer and
     * @type {(function(*, *): Promise<void>)}
     */
    const navigateToInformationView = useCallback(async (customer, orderId) => {
        const customerId = await fetchCustomerIdFromCode(customer, orderId);
        if (!customerId || !isMounted())
            return;
        if (allowPayments) {
            history.push(routeFunctions.customer.accounting.invoices.single(customerId, orderId));
        } else {
            history.push(routeFunctions.customer.sales.invoices.single(customerId, orderId));
        }
    }, [fetchCustomerIdFromCode, isMounted, history, allowPayments])

    /**
     * Navigates to the print view of the given customer and order.
     * @type {(function(*, *): Promise<void>)}
     */
    const navigateToPrintView = useCallback(async (customer, orderId) => {
        const customerId = await fetchCustomerIdFromCode(customer, orderId);
        if (!customerId || !isMounted())
            return

        window.open(window.location.origin + stringifyUrl({
            url: routeFunctions.print.invoice(customerId, orderId),
            query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: true}
        }), '_blank');

        // setPrintOrderConfigurationsDialog({
        //     open: true,
        //     printUrl: routeFunctions.print.invoice(customerId, orderId),
        //     onClose: () => setPrintOrderConfigurationsDialog(initPrintOrderConfigurationsDialogState)
        // })
    }, [fetchCustomerIdFromCode, isMounted])

    return (
        <>
            <div className='invoices-list-view sales-views-top-progress-indicator-offset'>
                <div className={'d-flex justify-content-between align-content-center mb-2'}>
                    <p className={'view-title'}>
                        {defaultCustomer?.name ? `${defaultCustomer?.name}'s` : "System"} Invoices
                    </p>
                    <div className={'d-flex align-content-center justify-content-end'}>
                        {
                            !!defaultCustomer?.id &&
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                component={Link}
                                to={allowPayments ? routes.accounting.invoices : routes.sales.invoices}
                            >
                                View all invoices
                            </Button>
                        }
                    </div>
                </div>
                <div className={'d-flex text-xs text-color-3 mb-3'}>
                    {
                        operatorAccessLevels.packages.sales.invoiceMultiPayments &&
                        (

                            allowPayments
                                ? !!defaultCustomer?.id
                                    ? ''
                                    : '* Please ensure only a single customer is selected to enable multi-payment feature.'
                                : '* if you want to pay for a customer\'s invoices, please navigate to invoices list in accounting section'
                        )
                    }
                </div>
                <OrderSearchSection
                    searchData={searchData}
                    showPartNumberField={searchData.tab === tabs.items}
                    hideCustomersMultiSelect={!!defaultCustomer?.id}
                />
                <div className='my-2'/>
                <div className='w-100 flex-grow-1'>
                    <Tabs value={searchData.tab} onChange={onTabChanged}>
                        {
                            tabsList.map(e => (
                                <Tab key={e.value} value={e.value} label={e.title}/>
                            ))
                        }
                    </Tabs>
                </div>
                <div className='my-4 w-100'/>
                {searchData.tab === tabs.report &&
                    <InvoicesListViewReportTable
                        exportToCsv={canExportInvoicesList ? exportToCsv : undefined}
                        searchData={searchData}
                        data={data}
                        dataGridSaveConfig={reportDataGridSaveConfig}
                        navigateToInformationView={navigateToInformationView}
                        navigateToPrintView={navigateToPrintView}
                        fetchingOrdersCustomer={fetchingOrdersCustomer}
                    />
                }
                {searchData.tab === tabs.items &&
                    <InvoicesListViewItemsTable
                        exportToCsv={canExportInvoicesList ? exportToCsv : undefined}
                        searchData={searchData}
                        data={data}
                        dataGridSaveConfig={itemsDataGridSaveConfig}
                        navigateToInformationView={navigateToInformationView}
                        navigateToPrintView={navigateToPrintView}
                        fetchingOrdersCustomer={fetchingOrdersCustomer}
                    />
                }
                {searchData.tab === tabs.orders &&
                    <InvoicesListViewOrdersTable
                        exportToCsv={canExportInvoicesList ? exportToCsv : undefined}
                        searchData={searchData}
                        data={data}
                        allowPayments={allowPayments}
                        fetchOrders={fetchOrders}
                        search={searchOrders}
                        dataGridSaveConfig={ordersDataGridSaveConfig}
                        navigateToInformationView={navigateToInformationView}
                        navigateToPrintView={navigateToPrintView}
                        fetchingOrdersCustomer={fetchingOrdersCustomer}
                    />
                }
            </div>
            <CreateUserOnlineProfileDialog
                open={onlineUserProfileDialog?.open}
                initialValues={onlineUserProfileDialog.initialValues}
                onSuccess={onlineUserProfileDialog.onSuccess}
                setOpen={onlineUserProfileDialog.onClose}
            />
            <PrintOrderConfigurationsDialog
                open={printOrderConfigurationsDialog.open}
                onClose={printOrderConfigurationsDialog.onClose}
                printUrl={printOrderConfigurationsDialog.printUrl}
            />
        </>
    );
}

export default InvoicesListView;
